import React, { useState, FC } from 'react';
import { makeStyles } from 'design-system/styles';
import { Alert, Popover, Button, TextButton } from 'design-system';
import Dialog, { DialogTitle, DialogContent } from 'react-scale/components/design-system/Dialog';
import { TeamExplorer } from 'react-scale/corp/views/qm/team/components/TeamExplorer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { STAGE, DEFAULT_TEAM, STAGE_TITLE } from './constants';
import AssignTeamView from './components/AssignTeamView';
import MainView from './components/MainView';
import CreateTeamView from './components/CreateTeamView';

interface TeamSelectorProps {
  team: { id: string; name: string };
  setTeam: React.Dispatch<React.SetStateAction<{ id: string; name: string }>>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isOpen: boolean;
}

const TeamSelector: FC<TeamSelectorProps> = ({ team, setTeam, isOpen, setIsOpen }) => {
  return (
    <Popover
      tooltip={'View Worker Team Directory'}
      targetOpen={
        <Button fullWidth endIcon={<FontAwesomeIcon icon="caret-up" />}>
          {team.name}
        </Button>
      }
      targetClosed={
        <Button fullWidth endIcon={<FontAwesomeIcon icon="caret-down" />}>
          {team.name}
        </Button>
      }
      width={600}
      maxHeight={450}
      isOpen={isOpen}
    >
      <TeamExplorer
        teamId={team.id}
        TeamSelectButton={() => <TextButton type="primary">+</TextButton>}
        onSelectTeam={(id, name) => {
          setTeam({ id, name });
          setIsOpen(false);
        }}
      />
    </Popover>
  );
};

interface TeamAssignmentModalProps {
  setRefetchState: React.Dispatch<React.SetStateAction<number>>;
  showTeamAssignmentDialog: boolean;
  setShowTeamAssignmentDialog: React.Dispatch<React.SetStateAction<boolean>>;
  projectId?: string;
}

const TeamAssignmentModal: FC<TeamAssignmentModalProps> = ({
  setRefetchState,
  showTeamAssignmentDialog,
  setShowTeamAssignmentDialog,
  projectId,
}) => {
  const classes = useStyles();
  const [currentStage, setCurrentStage] = useState<STAGE>(STAGE.MAIN_VIEW);
  const [alterStatus, setAlertStatus] = useState<{ severity: any; content: any } | null>(null);
  const [addedwokerTeams, setAddedWorkerTeams] = useState<{
    primary: Array<string>;
    secondary: Array<string>;
  }>({ primary: [], secondary: [] });

  const handleClose = () => {
    setShowTeamAssignmentDialog(false);
    if (addedwokerTeams.primary.length || addedwokerTeams.secondary.length) {
      setRefetchState(prevState => ++prevState);
      setAddedWorkerTeams({ primary: [], secondary: [] });
    }
  };

  const [team, setTeam] = useState<{ id: string; name: string }>({
    id: DEFAULT_TEAM.id,
    name: DEFAULT_TEAM.name,
  });
  const [isOpen, setIsOpen] = useState<boolean>(false);

  if (!showTeamAssignmentDialog) {
    return null;
  }

  const props = {
    setCurrentStage,
    setAlertStatus,
    team,
    setTeam,
    TeamSelector() {
      return <TeamSelector team={team} setTeam={setTeam} isOpen={isOpen} setIsOpen={setIsOpen} />;
    },
  };

  return (
    <Dialog open={showTeamAssignmentDialog}>
      <DialogTitle className={classes.header}>
        {currentStage !== STAGE.MAIN_VIEW && (
          <TextButton onClick={() => setCurrentStage(STAGE.MAIN_VIEW)}>
            <FontAwesomeIcon icon="caret-left" />
          </TextButton>
        )}
        <div>{STAGE_TITLE[currentStage]}</div>
        <TextButton className={classes.right} onClick={handleClose}>
          ✕
        </TextButton>
      </DialogTitle>
      <DialogContent>
        {alterStatus && (
          <div style={{ width: 400 }}>
            <Alert severity={alterStatus.severity}>{alterStatus.content}</Alert>
          </div>
        )}
        {currentStage === STAGE.MAIN_VIEW && (
          <MainView {...props} addedwokerTeams={addedwokerTeams} />
        )}
        {currentStage === STAGE.CREATE_TEAM_VIEW && <CreateTeamView {...props} />}
        {currentStage === STAGE.ASSIGN_TEAM_VIEW && (
          <AssignTeamView
            {...props}
            projectId={projectId}
            setAddedWorkerTeams={setAddedWorkerTeams}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles({
  mainViewWrapper: {
    display: 'flex',
  },

  header: {
    '& h2': {
      display: 'flex',
    },
    '& span': {
      fontSize: 20,
      color: 'var(--color-RemoBlue)',
    },
  },

  right: {
    '& span': {
      fontSize: 20,
    },
    marginLeft: 'auto',
  },
});

export default TeamAssignmentModal;
